import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    background-color: #f6f3ef;
    color: #333;
    scroll-behavior: smooth;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Hero = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: linear-gradient(135deg, #b2cbe0 0%, #f6f3ef 100%);
  color: #2b4b5a;
  animation: ${fadeIn} 1.5s ease-out;
`;

const HeroTitle = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: 3rem;
  margin: 0;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-top: 1rem;
  max-width: 600px;
`;

const ScrollArrow = styled.div`
  margin-top: 3rem;
  font-size: 2rem;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
`;

const Card = styled.div`
  background: #fff;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  animation: ${fadeIn} 1.2s ease-out;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  text-align: center;
  font-family: "Playfair Display", serif;
  color: #2b4b5a;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Checkbox = styled.input`
  margin-top: 4px;
`;

const WaiverBox = styled.div`
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #555;
  max-height: 250px;
  overflow-y: scroll;
`;

const Button = styled.button`
  background-color: #2b4b5a;
  color: #fff;
  padding: 0.75rem;
  font-size: 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 1rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  text-align: center;
  margin-top: 1rem;
  color: #2b4b5a;
  font-weight: bold;
`;

const Section = styled.div`
  max-width: 600px;
  width: 100%;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h3`
  text-align: center;
  font-family: "Playfair Display", serif;
  color: #2b4b5a;
  margin-bottom: 1.5rem;
`;

const Question = styled.button`
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  font-size: 1rem;
  padding: 0.75rem 0;
  color: #2b4b5a;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 1px solid #eee;

  &:hover {
    color: #1c303c;
  }
`;

const Answer = styled.div`
  padding: 0.5rem 0 1rem 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
`;

export default function PilatesRegistration() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agree, setAgree] = useState(false);
  const [message, setMessage] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [registrationCount, setRegistrationCount] = useState(0);

  useEffect(() => {
    // Fetch current registration count from Google Sheet
    // (This assumes your Apps Script can return the row count)
    fetch(
      "https://script.google.com/macros/s/AKfycbwCZIsCt2cs0DIWTLmESHbJlBITaRliDff7uNT19J7ze9tjYYV2U5mrtXNKRAdtYfKYDA/exec"
    )
      .then((res) => res.json())
      .then((data) => setRegistrationCount(data.count || 0))
      .catch(() => setRegistrationCount(0));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agree) return;

    try {
      // Always re-fetch live registration count before submitting
      const res = await fetch(
        "https://script.google.com/macros/s/AKfycbwCZIsCt2cs0DIWTLmESHbJlBITaRliDff7uNT19J7ze9tjYYV2U5mrtXNKRAdtYfKYDA/exec"
      );
      const data = await res.json();
      const currentCount = data.count || 0;

      if (currentCount >= 15) {
        await addToWaitlist();
        setMessage(
          "The class is currently full — you've been added to the waitlist! You'll be notified if a spot opens up."
        );
        return;
      }

      // Otherwise, register normally
      const scriptURL =
        "https://script.google.com/macros/s/AKfycbwCZIsCt2cs0DIWTLmESHbJlBITaRliDff7uNT19J7ze9tjYYV2U5mrtXNKRAdtYfKYDA/exec";
      const payload = { Name: name, Email: email };

      await fetch(scriptURL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setMessage("Thanks for registering! See you on Monday");
      setName("");
      setEmail("");
      setAgree(false);
      setRegistrationCount(currentCount + 1);
    } catch (error) {
      setMessage("There was an error submitting the form. Please try again.");
    }
  };

  async function addToWaitlist() {
    const waitlistURL =
      "https://script.google.com/macros/s/AKfycby23MlX_PLQlFAMVLke0XMd28IiKzG2SnQKmholcNM1BEKDu6_P6lbSUWsfuRXPMWhE/exec";
    const payload = { Name: name, Email: email };

    try {
      await fetch(waitlistURL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Waitlist submission failed:", err);
    }
  }

  const faqs = [
    {
      question: "How long is each class?",
      answer:
        "Classes run approximately 45 minutes to one hour. I’ll be offering them once a week to start, with hopes to expand to twice weekly. Classes will run until the end of November.",
    },
    {
      question: "Can I bring my baby?",
      answer:
        "I’m so sorry — I’m not able to accommodate babies in class at this time. Access to Pilates for mothers is very important to me, and I hope to offer a mom-friendly option in the future. I’ll be sure to email when that becomes available.",
    },
    {
      question: "Are your classes suitable for seniors?",
      answer:
        "Absolutely! Everyone is welcome. Since I’m still completing my certification, please check with your doctor if you’re unsure about your ability to participate. If you don’t have acute injuries or significant pain, you’re encouraged to come and try the class.",
    },
    {
      question: "Do you welcome constructive feedback?",
      answer:
        "Yes, please! I truly appreciate kind, constructive feedback — it helps me grow as an instructor. Just be gentle with me, I’m learning too!",
    },
    {
      question: "I’m a beginner or not very flexible — can I still join?",
      answer:
        "Yes! These classes are beginner-friendly and will gently progress toward some intermediate movements. There will always be options to modify so you can move in the way that feels best for your body. It’s all about progress, not perfection — I’m working on my flexibility too!",
    },
  ];

  return (
    <>
      <GlobalStyle />
      <Hero>
        <HeroTitle>Mat Pilates</HeroTitle>
        <HeroSubtitle>
          Free Mat Classes in Bloor West Village
          <br />
          Move with mindfulness and community — join Natalie for free Pilates
          mat classes on Mondays.
        </HeroSubtitle>
        <ScrollArrow>↓</ScrollArrow>
      </Hero>

      <Container id="form-section">
        <Card>
          <Title>Pilates Mat Class Registration</Title>
          <Subtitle>
            <p
              style={{
                textAlign: "center",
                color: "#2b4b5a",
                fontWeight: "600",
              }}
            >
              <span style={{ fontSize: "1.1rem", color: "#1c303c" }}>
                Monday November&nbsp;3rd
              </span>{" "}
              <strong>@ 1:30–2:20&nbsp;pm</strong> ·{" "}
              <span style={{ color: "#1c303c", fontWeight: "700" }}>
                Swansea Town Hall
              </span>{" "}
              ·{" "}
              <span style={{ color: "#8B0000", fontWeight: "700" }}>
                Teiaiagon Room, 2nd Floor
              </span>
            </p>
            <br />
            I'm an almost-certified Pilates instructor offering free mat Pilates
            classes in Bloor West Village.
          </Subtitle>
          <p
            style={{ textAlign: "center", color: "#2b4b5a", fontWeight: "600" }}
          >
            Current spots filled: {registrationCount} / 15
          </p>
          {registrationCount >= 15 ? (
            <>
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  backgroundColor: "#fff8f8",
                  border: "1px solid #f5cccc",
                  borderRadius: "12px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                }}
              >
                <h2 style={{ color: "#b22222", marginBottom: "1rem" }}>
                  Class Full
                </h2>
                <p
                  style={{
                    color: "#444",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  All <strong>15 spots</strong> are currently taken. But don’t
                  worry — you can still join the waitlist below, and we’ll email
                  you if a spot opens up!
                </p>

                <Form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await addToWaitlist();
                    setMessage("You've been added to the waitlist! Thank you.");
                    setName("");
                    setEmail("");
                    setAgree(false);
                  }}
                >
                  <Input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <Button type="submit" style={{ backgroundColor: "#b22222" }}>
                    Join Waitlist
                  </Button>
                </Form>
              </div>
              {message && <Message>{message}</Message>}
            </>
          ) : (
            <>
              <Form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />

                <WaiverBox>
                  <strong>Waiver & Consent</strong>
                  <p>
                    By participating in these Pilates classes offered by
                    instructor <strong>Natalie</strong>, I acknowledge and agree
                    to the following:
                  </p>
                  <ol>
                    <li>
                      I understand that Natalie is a Pilates instructor in
                      training and not yet certified.
                    </li>
                    <li>
                      I recognize that Pilates involves physical movement and
                      exercise, which carries inherent risks including, but not
                      limited to, muscle strain, injury, or aggravation of
                      existing conditions.
                    </li>
                    <li>
                      I confirm that I am in good physical health and have
                      consulted a medical professional if I have any concerns
                      about my ability to participate safely.
                    </li>
                    <li>
                      I voluntarily assume all risk of injury or harm associated
                      with participation in these classes.
                    </li>
                    <li>
                      I release and hold harmless Natalie, Swansea Town Hall,
                      and any affiliates or partners from any and all liability,
                      claims, or damages that may arise from participation.
                    </li>
                    <li>
                      I consent to participate and understand that my
                      participation is voluntary.
                    </li>
                  </ol>
                </WaiverBox>

                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    required
                  />
                  <label>
                    I have read and agree to the waiver and consent terms.
                  </label>
                </CheckboxContainer>

                <Button type="submit" disabled={!agree}>
                  Register for Class on Monday, November 3rd
                </Button>
              </Form>
              {message && <Message>{message}</Message>}
            </>
          )}
        </Card>

        <Section>
          <SectionTitle>About Me</SectionTitle>
          <p style={{ lineHeight: "1.6", color: "#555" }}>
            Hi! I’m <strong>Natalie</strong> — a Pilates instructor-in-training
            based in Bloor West Village. I discovered Pilates 12 years ago while
            in University but really fell in love with it while pregnant and
            postpartum with my first. I love how it builds strength, balance,
            and calm. My goal is to create a welcoming, encouraging space where
            people of all levels can move mindfully and feel good in their
            bodies. I’m completing my certification next month and am so excited
            to share what I’ve learned with my community! I live with my husband
            and almost 2 year old in the neighbourhood. You may see me at
            Hummingbird Field doing hot yoga or Pilates. I am also currently
            entering my second trimester — these will not be pregnancy-friendly
            classes. But I look forward to using what I’m learning about cueing
            to make the classes fun and challenging for all. Thanks for being
            here! I look forward to meeting you.
          </p>
        </Section>

        <Section>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          {faqs.map((faq, index) => (
            <div key={index}>
              <Question
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                {faq.question}
              </Question>
              {openIndex === index && <Answer>{faq.answer}</Answer>}
            </div>
          ))}
        </Section>
        <Section>
          <SectionTitle>Contact</SectionTitle>
          <p style={{ textAlign: "center", color: "#555", lineHeight: "1.6" }}>
            Have a question or want to get in touch? I’d love to hear from you!
            <br />
            You can reach me anytime at{" "}
            <a
              href="mailto:bwvpilates@gmail.com"
              style={{
                color: "#2b4b5a",
                textDecoration: "none",
                fontWeight: "600",
              }}
            >
              bwvpilates@gmail.com
            </a>
            .
          </p>
        </Section>
      </Container>
    </>
  );
}
